/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useIcons } from 'src/core/hooks/useIcons';
import { logWizardDismiss } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureCsAccountKind, AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { tryParseJson } from 'src/core/utils/base';
import { isCognitiveServicesResource, isTextAnalyticsResource } from 'src/core/utils/csAccountUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { LanguageStudioOnboardingModal } from 'src/languageStudio/appShell/landingPage/components/LanguageStudioOnboardingModal';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

/**
 * Startup config for the unified language portal page.
 */
export const StudioStartupConfig = observer(() => {
    useIcons();
    const {
        uiStore,
        dispatcher,
        rootStore: { azureStore, authStore }
    } = useGlobalState();

    const [isOnboardingHidden, setIsOnboardingHidden] = React.useState(true);

    const storage = getStorage('localStorage');
    const currentSelectedAuthoringResource = azureStore.session.csAccountStore;
    const isOnboardingModalHidden = !authStore.isSignedIn || isOnboardingHidden;

    const { subPath } = getPortalFromUrl();

    let serviceTarget: AzureCsAccountKind;

    if (subPath === 'translation') {
        serviceTarget = 'TextTranslation';
    } else {
        serviceTarget = 'TextAnalytics';
    }

    const isOnboardingModalSeen = () =>
        tryParseJson<boolean>(storage.getData(LuisConstants.StorageKeys.LanguageStudioOnboardingModalSeen)) ?? false;

    const isInvalidResource = (r: AzureCsAccountStore) =>
        !r ||
        !r.path ||
        !(
            isTextAnalyticsResource(r) ||
            isCognitiveServicesResource(r) ||
            ((FeatureToggles.DocumentTranslationTool || FeatureToggles.DocumentTranslationTryOut || FeatureToggles.TextTranslationTryOut) &&
                AzureResourcesStrategies.translatorResourceStrategy(r))
        );

    const changeModalVisibility = (isVisible: boolean) => {
        dispatcher.dispatch('updateCsAccountSelectModalVisibility', { isVisible });

        if (!isVisible && !currentSelectedAuthoringResource) {
            dispatcher.dispatch('updateCsAccountTeachingBubbleVisibility', { isVisible: true });
        }
    };

    React.useEffect(() => {
        dispatcher.dispatch('getAzureSubscriptions');
        setIsOnboardingHidden(isOnboardingModalSeen());
    }, [currentSelectedAuthoringResource?.id]);

    React.useEffect(() => {
        if (
            isOnboardingHidden &&
            isOnboardingModalSeen() &&
            uiStore.isSwitchAccountModalHidden &&
            isInvalidResource(currentSelectedAuthoringResource)
        ) {
            changeModalVisibility(true);
        }
    }, [isOnboardingHidden, currentSelectedAuthoringResource?.id]);

    React.useEffect(() => {
        if (
            isOnboardingHidden &&
            isOnboardingModalSeen() &&
            !currentSelectedAuthoringResource &&
            uiStore.isSwitchAccountModalHidden &&
            uiStore.isNoAccountTeachingBubbleHidden
        ) {
            dispatcher.dispatch('updateCsAccountTeachingBubbleVisibility', { isVisible: true });
        }
    }, [currentSelectedAuthoringResource, isOnboardingHidden, uiStore.isSwitchAccountModalHidden]);

    const handleOnboardingCompletion = () => {
        setIsOnboardingHidden(true);
        storage.setData(LuisConstants.StorageKeys.LanguageStudioOnboardingModalSeen, 'true');
        changeModalVisibility(isInvalidResource(currentSelectedAuthoringResource) && uiStore.isSwitchAccountModalHidden);
    };

    return (
        <Stack>
            {!uiStore.isSwitchAccountModalHidden && (
                <AzureResourcesManager
                    serviceTarget={serviceTarget}
                    selectedResourceId={uiStore.selectedResourceId}
                    onChangeSelectResourceVisible={changeModalVisibility}
                    selectedSubscriptionId={uiStore.selectedSubscriptionId}
                    isSelectResourceVisible={!uiStore.isSwitchAccountModalHidden}
                    strategies={{ resource: AzureResourcesStrategies.languageStudioResourceStrategy }}
                />
            )}

            <LanguageStudioOnboardingModal
                hidden={isOnboardingModalHidden}
                onSubmit={handleOnboardingCompletion}
                onDismiss={() => {
                    setIsOnboardingHidden(true);
                    logWizardDismiss();
                }}
            />
        </Stack>
    );
});
